import { noop } from "@mantine/core";
import { usePrevious } from "@mantine/hooks";
import { useEffect, useEffectEvent, useRef, useState } from "react";

import { isRightTopTab, type RightTopTab, sidecarTabEventBus } from "./sidecarTabEventBus";

interface UseRightTopTabStateParams {
    showAssistTab: boolean;
    customerSession: boolean;
    isVerified: boolean;
    showOffersTab: boolean;
    /** Whether a guide is open; it renders inline at the top of the AI Assist tab. */
    isGuideOpen: boolean;
    /**
     * Optional persistence adapter. When `initialTab` is provided (chat), it is restored on mount when
     * selectable, and the mount-time auto-select is suppressed so it doesn't overwrite the restored tab.
     * `onTabChange` reports the selected tab so the consumer can persist it. Voice omits both → ephemeral
     * local state, identical to the pre-hook behavior.
     */
    initialTab?: RightTopTab;
    onTabChange?: (tab: RightTopTab) => void;
}

interface UseRightTopTabStateResult {
    activeTab: RightTopTab;
    handleTabChange: (value: string | null) => void;
    /** Briefly true after Customer/Offers become enabled, to pulse the newly-available tabs. */
    tabsJustEnabled: boolean;
}

/**
 * Owns the right-top tab selection: the default/auto-switch rules (customer → Customer, verify → AI
 * Assist), the imperative focus-bus, and — when a persistence adapter is passed — per-consumer restore
 * + write-back. Shared by voice (ephemeral) and chat (per-chat persisted).
 *
 * A guide opening after verification brings the expert to AI Assist, where it renders inline; one opened
 * before verification waits for the verify switch, and one already open at first render (e.g. a chat
 * restoring a session mid-guide) leaves the restored/default tab in place.
 */
export function useSidecarTabState({
    showAssistTab,
    customerSession,
    isVerified,
    showOffersTab,
    isGuideOpen,
    initialTab,
    onTabChange = noop,
}: UseRightTopTabStateParams): UseRightTopTabStateResult {
    // First *selectable* tab in priority order — the default and the fallback when the desired tab
    // isn't available, so we never land on a hidden/disabled tab. (Offers is subsumed by Customer.)
    const firstEnabledTab: RightTopTab = showAssistTab ? "ai-assist" : customerSession ? "customer" : "ai-assist";

    const isTabSelectable = (tab: RightTopTab): boolean => {
        switch (tab) {
            case "ai-assist":
                return showAssistTab;
            case "customer":
                return customerSession;
            case "offers":
                return showOffersTab && customerSession && isVerified;
        }
    };

    // Restore a persisted tab on mount when it's still selectable; otherwise fall back to the default.
    // While a valid tab is restored, suppress the mount-time auto-select effects so they don't overwrite
    // it. With no `initialTab` (voice) `restoredTab` is undefined → ref stays false → effects run as before.
    //
    // Assumes a saved tab's gating flags are already loaded (cached) by the time the tab exists — true for
    // in-memory persistence. If tabs ever move to durable storage, restore would need to retry once flags load.
    const restoredTab = initialTab && isTabSelectable(initialTab) ? initialTab : undefined;
    const [activeTab, setActiveTab] = useState<RightTopTab>(restoredTab ?? firstEnabledTab);
    const skipMountAutoSelectRef = useRef(restoredTab !== undefined);

    const previousCustomerSession = usePrevious(customerSession);
    const previousIsVerified = usePrevious(isVerified);
    const previousIsGuideOpen = usePrevious(isGuideOpen);
    const [tabsJustEnabled, setTabsJustEnabled] = useState(false);

    useEffect(() => {
        if (previousCustomerSession === customerSession) return;
        if (skipMountAutoSelectRef.current) return;
        setActiveTab(customerSession ? "customer" : firstEnabledTab);
        if (customerSession && previousCustomerSession === false) {
            setTabsJustEnabled(true);
            const timer = setTimeout(() => setTabsJustEnabled(false), 800);
            return () => clearTimeout(timer);
        }
    }, [customerSession, previousCustomerSession, firstEnabledTab]);

    useEffect(() => {
        if (!isVerified || previousIsVerified) return;
        if (skipMountAutoSelectRef.current) return;
        setActiveTab(firstEnabledTab);
    }, [isVerified, previousIsVerified, firstEnabledTab]);

    // `undefined` = first render (guide already open at mount, e.g. a chat restoring mid-guide), which keeps
    // the restored/default tab. Unverified, the verify effect above brings the expert to AI Assist instead.
    useEffect(() => {
        if (!isGuideOpen || previousIsGuideOpen !== false || !showAssistTab || !isVerified) return;
        setActiveTab("ai-assist");
    }, [isGuideOpen, previousIsGuideOpen, showAssistTab, isVerified]);

    // Clear the mount suppression after the first commit so later real transitions auto-switch.
    useEffect(() => {
        skipMountAutoSelectRef.current = false;
    }, []);

    // Let imperative actions outside this tree focus a tab — e.g. Quick rebuttal pushing a message
    // into the AI Assist timeline needs the AI Assist tab brought forward so the expert sees it.
    useEffect(() => sidecarTabEventBus.on("focus-tab", setActiveTab), []);

    // Report every change (clicks, auto-switch, focus-bus) to the persistence adapter. useEffectEvent
    // keeps onTabChange out of the deps so the effect fires only on a real tab change.
    const reportTabChange = useEffectEvent(onTabChange);
    useEffect(() => reportTabChange(activeTab), [activeTab]);

    const handleTabChange = (value: string | null) => {
        if (!isRightTopTab(value)) return;
        setActiveTab(value);
    };

    return { activeTab, handleTabChange, tabsJustEnabled };
}
